import { z } from "zod";
import {
  emailField,
  confirmEmailField,
  firstNameField,
  lastNameField,
  organizationsField,
  optionalMessageField,
} from "@core/schemas/fields";
import { withEmailConfirmation } from "@core/schemas/refinements";
import { useForm } from "@core/features/form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { Dialog } from "@themes/pollinator/ui/Dialog";

export const newsletterSchema = withEmailConfirmation(
  z.object({
    firstName: firstNameField,
    lastName: lastNameField,
    email: emailField,
    confirmEmail: confirmEmailField,
    organizations: organizationsField,
    message: optionalMessageField,
  })
);

export type NewsletterFormData = z.infer<typeof newsletterSchema>;

export type Props = {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when the dialog should close */
  onClose: () => void;
  /** Called when the form is submitted with valid data */
  onSubmit: (data: NewsletterFormData) => Promise<void>;
};

/**
 * A dialog form for users to subscribe to the newsletter.
 * This is a pure presentational component - all business logic (API calls,
 * event tracking, dialog state) should be handled by the parent.
 *
 * @example
 * <MarketingNewsletterDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSubmit={async (data) => {
 *     await apiRequest("/api/newsletter", "POST", data);
 *   }}
 * />
 */
export function MarketingNewsletterDialog({ isOpen, onClose, onSubmit }: Props) {
  const { form, handleSubmit, handleClose } = useForm({
    schema: newsletterSchema,
    onSubmit,
    onClose,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      organizations: "",
      message: "",
    },
  });

  const { isSubmitting, isValid, isSubmitSuccessful } = form.formState;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={isSubmitSuccessful ? "Success!" : "Get Updates"}
      description={
        isSubmitSuccessful
          ? "Thank you for subscribing to our newsletter. We'll keep you updated on our latest news and opportunities."
          : "Sign up for our newsletter to stay informed about Pollinator's progress and opportunities."
      }
      isWithActions={!isSubmitSuccessful}
      primaryButtonText="Submit"
      secondaryButtonText="Cancel"
      onPrimaryClick={handleSubmit}
      onSecondaryClick={handleClose}
      primaryButtonProps={{
        disabled: isSubmitting,
        visuallyDisabled: !isSubmitting && !isValid,
        type: "submit",
      }}
      secondaryButtonProps={{
        disabled: isSubmitting,
      }}
    >
      {!isSubmitSuccessful && (
        <Form form={form} className="py-4">
          <FormField
            name="firstName"
            label="First Name"
            component="Input"
            placeholder="Enter your first name"
            required
          />
          <FormField
            name="lastName"
            label="Last Name"
            component="Input"
            placeholder="Enter your last name"
            required
          />
          <FormField
            name="email"
            label="Email"
            component="Input"
            placeholder="Enter your email"
            autoComplete="email"
            required
          />
          <FormField
            name="confirmEmail"
            label="Email Confirmation"
            component="Input"
            placeholder="Confirm your email"
            required
          />
          <FormField
            name="organizations"
            label="Alumni Affiliations"
            component="Textarea"
            placeholder="What BFA / MFA / residencies / fellowships / art programs have you participated in?"
            required
          />
          <FormField
            name="message"
            label="Message"
            component="Textarea"
            placeholder="Your message"
          />
        </Form>
      )}
    </Dialog>
  );
}
